// UI projections only. Server proposal IDs and explicit write scopes retain authority.
export function chaseProposalRows(queue = []) {
  const groups = new Map();
  for (const patch of queue) {
    if (!groups.has(patch.pid)) groups.set(patch.pid, { ...patch, patches: [], blockIds: [] });
    const row = groups.get(patch.pid);
    row.patches.push(patch);
    if (!row.blockIds.includes(patch.blockId)) row.blockIds.push(patch.blockId);
    row.stale ||= patch.stale;
  }
  return [...groups.values()].map(row => ({ ...row,
    label: row.patches.map(patch => patch.label).join(', '),
    scopeLabel: row.blockIds.includes('document') ? 'whole file' : `${row.blockIds.length} region${row.blockIds.length === 1 ? '' : 's'}`,
  }));
}

export function chaseMinorScope(blocks = []) {
  const ids = [...new Set(blocks.filter(block => block.identity === 'authored' && block.qa === 'minor').map(block => block.id))];
  if (!ids.length || ids.length > 16) return { enabled: false, count: ids.length };
  return { enabled: true, count: ids.length, scope: ids.length === 1 ? 'region' : 'multi', regionIds: ids };
}

export function chaseAcceptLabel(patches = []) {
  const ids = new Set(patches.map(patch => patch.blockId ?? patch.region_id));
  return `Accept proposal · ${ids.has('document') ? 'whole file' : `${ids.size} region${ids.size === 1 ? '' : 's'}`}`;
}

export function chaseQuickPrompts(scope, block, intent) {
  if (intent === 'explain') return scope === 'document'
    ? ['Explain this build', 'Summarize the open findings', 'Explain the responsive layout']
    : ['Explain this finding', 'Explain the implementation', 'Check mobile wrapping'];
  if (scope === 'document') return ['Address the open findings', 'Match the design spacing', 'Fix mobile overflow'];
  if (scope === 'multi') return ['Address the selected findings', 'Align the selected sections', 'Fix mobile wrapping'];
  if (['hero', 'image', 'ratetable'].includes(block?.type || block?.id)) return ['Extend the alt text', 'Match the image spacing', 'Check mobile scaling'];
  return ['Address this finding', 'Match the design spacing', 'Fix mobile wrapping'];
}

export function chaseFindingScope(blocks, id) {
  if (id === 'document') return { scope: 'document' };
  return blocks?.some(block => block.id === id && block.identity === 'authored') ? { scope: 'region', blockId: id } : null;
}

export function chaseFindingProgress(chase, operationId) {
  if (!operationId) return null;
  const op = chase?.operations?.find(item => item.operation_id === operationId);
  const proposal = [...(chase?.proposals || []), ...(chase?.retainedProposals || [])]
    .find(item => item.op_id === operationId || op?.proposal_id && item.proposal_id === op.proposal_id);
  if (proposal) return proposal.status === 'proposed' ? 'Patch staged' : 'Proposal ' + proposal.status;
  return op?.text || (op?.status ? 'Chase ' + op.status : 'Request submitted');
}

// Combined preview and Accept all share the same region/base conflict rules.
export function chaseProposalSet(proposals = [], revision, digest) {
  if (proposals.length < 2 || proposals.length > 16) return { enabled: false, reason: 'Choose 2–16 staged proposals.' };
  const ids = new Set(), regions = new Set(), patches = [];
  for (const proposal of proposals) {
    if (ids.has(proposal.proposal_id)) return { enabled: false, reason: 'Duplicate proposal in this set.' };
    ids.add(proposal.proposal_id);
    if (proposal.status !== 'proposed' || proposal.base?.revision !== revision || proposal.base?.revision_digest !== digest)
      return { enabled: false, reason: 'The staged set is stale. Refresh proposals.' };
    if (proposal.scope?.kind === 'document') return { enabled: false, reason: 'Whole-file proposals must be reviewed separately.' };
    for (const patch of proposal.patches || []) {
      if (regions.has(patch.region_id)) return { enabled: false, reason: 'Proposals overlap in ' + patch.region_id + '. Review them separately.' };
      regions.add(patch.region_id); patches.push(patch);
    }
  }
  if (!regions.size || regions.size > 16) return { enabled: false, reason: 'Combined scope must contain 1–16 regions.' };
  return { enabled: true, proposalIds: [...ids].sort(), regionIds: [...regions].sort(), patches,
    label: `${proposals.length} proposals · ${regions.size} blocks` };
}
